// frontend/src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const safeStr = (v) => String(v || '').trim();

const getUserSiteObj = (user) => (user?.site && typeof user.site === 'object' ? user.site : null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || null;
    } catch {
      return null;
    }
  });

  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('userData');
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  /**
   * Persist auth + site context in ONE place.
   * NOTE: UI should never display siteId; keep siteId only for API requests.
   */
  const persistSession = (jwt, userObj, siteCtx = {}) => {
    try {
      // token + user
      if (jwt) localStorage.setItem('token', jwt);
      else localStorage.removeItem('token');

      if (userObj) localStorage.setItem('userData', JSON.stringify(userObj));
      else localStorage.removeItem('userData');

      // site context
      const siteId = siteCtx?.siteId ? String(siteCtx.siteId) : '';
      const siteCode = safeStr(siteCtx?.siteCode);
      const siteName = safeStr(siteCtx?.siteName);

      if (siteId) localStorage.setItem('selectedSiteId', siteId);
      else localStorage.removeItem('selectedSiteId');

      if (siteCode) localStorage.setItem('selectedSiteCode', siteCode);
      else localStorage.removeItem('selectedSiteCode');

      if (siteName) localStorage.setItem('selectedSiteName', siteName);
      else localStorage.removeItem('selectedSiteName');
    } catch {}

    setToken(jwt || null);
    setUser(userObj || null);
  };

  const clearSession = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('selectedSiteId');
      localStorage.removeItem('selectedSiteCode');
      localStorage.removeItem('selectedSiteName');
    } catch {}
    setToken(null);
    setUser(null);
  };

  const refreshMe = async () => {
    try {
      const res = await authAPI.getMe();
      const freshUser = res?.data?.user;
      if (freshUser) {
        // keep current site context from storage (don’t guess)
        persistSession(localStorage.getItem('token'), freshUser, {
          siteId: localStorage.getItem('selectedSiteId') || '',
          siteCode: localStorage.getItem('selectedSiteCode') || '',
          siteName: localStorage.getItem('selectedSiteName') || '',
        });
        return true;
      }
    } catch {
      // no auto-logout
    }
    return false;
  };

  /**
   * Directors -> {email,password}
   * Site users -> {email,password,siteId}
   *
   * After login, if backend provides populated user.site object:
   * we persist siteName/siteCode for display everywhere.
   */
  const login = async ({ email, password, siteId }) => {
    try {
      const { data } = await authAPI.login({ email, password, siteId });
      if (!data?.token || !data?.user) {
        return { success: false, message: 'Login response missing token/user.' };
      }

      const u = data.user;
      const role = safeStr(u?.role).toLowerCase();
      const siteObj = getUserSiteObj(u);

      // Persist site context only for site users (admin/director should be clean)
      if (role === 'admin' || role === 'director') {
        persistSession(data.token, u, { siteId: '', siteCode: '', siteName: '' });
      } else {
        // Site user: prefer populated user.site; fallback to selected siteId
        persistSession(data.token, u, {
          siteId: siteObj?._id || siteId || '',
          siteCode: siteObj?.code || localStorage.getItem('selectedSiteCode') || '',
          siteName: siteObj?.name || localStorage.getItem('selectedSiteName') || '',
        });
      }

      return { success: true };
    } catch (err) {
      const msg = err?.response?.data?.message || 'Login failed';
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    clearSession();
  };

  /**
   * Update site selection (only relevant for site platform)
   * Stores id + name + code (never display id in UI)
   */
  const setSelectedSite = ({ siteId, siteCode, siteName } = {}) => {
    try {
      if (siteId) localStorage.setItem('selectedSiteId', String(siteId));
      else localStorage.removeItem('selectedSiteId');

      if (siteCode) localStorage.setItem('selectedSiteCode', safeStr(siteCode));
      else localStorage.removeItem('selectedSiteCode');

      if (siteName) localStorage.setItem('selectedSiteName', safeStr(siteName));
      else localStorage.removeItem('selectedSiteName');
    } catch {}
  };

  // Effective siteId for API requests (OK to exist, but should NEVER be displayed)
  const effectiveSiteId = useMemo(() => {
    try {
      const sel = localStorage.getItem('selectedSiteId');
      if (sel) return sel;

      const s = user?.site;
      if (!s) return '';
      return typeof s === 'object' ? s._id || '' : String(s);
    } catch {
      return '';
    }
  }, [user]);

  // Effective siteCode for routes/labels
  const effectiveSiteCode = useMemo(() => {
    try {
      const fromStorage = safeStr(localStorage.getItem('selectedSiteCode'));
      if (fromStorage) return fromStorage;

      const siteObj = getUserSiteObj(user);
      return safeStr(siteObj?.code);
    } catch {
      return '';
    }
  }, [user]);

  // Effective siteName for labels
  const effectiveSiteName = useMemo(() => {
    try {
      const fromStorage = safeStr(localStorage.getItem('selectedSiteName'));
      if (fromStorage) return fromStorage;

      const siteObj = getUserSiteObj(user);
      return safeStr(siteObj?.name);
    } catch {
      return '';
    }
  }, [user]);

  const isAuthenticated = useMemo(() => Boolean(token && user), [token, user]);

  // ---- change password helper ----
  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      return { success: true };
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not change password';
      return { success: false, message: msg };
    }
  };

  // NOTE: changeEmail uses authAPI.changeEmail, but your api.js in this chat doesn't show it.
  // Keeping this function, but it will error unless you add the endpoint in api.js.
  const changeEmail = async ({ password, newEmail }) => {
    try {
      const { data } = await authAPI.changeEmail({ password, newEmail });
      if (data?.user) {
        persistSession(localStorage.getItem('token'), data.user, {
          siteId: effectiveSiteId,
          siteCode: effectiveSiteCode,
          siteName: effectiveSiteName,
        });
      }
      return { success: true };
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not change email';
      return { success: false, message: msg };
    }
  };

  const ctx = useMemo(
    () => ({
      user,
      token,
      isReady,
      isAuthenticated,

      // ✅ Expose these for UI (use these, not siteId)
      effectiveSiteId,
      effectiveSiteCode,
      effectiveSiteName,

      login,
      logout,
      refreshMe,

      // Accept object so you can store name/code too
      setSelectedSite,

      changePassword,
      changeEmail,
    }),
    [
      user,
      token,
      isReady,
      isAuthenticated,
      effectiveSiteId,
      effectiveSiteCode,
      effectiveSiteName,
    ]
  );

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
